/* vmx-search-predictive.js
   Live typeahead suggestions for the search form. Uses Shopify's built-in
   /search/suggest.json endpoint, so no third-party dependency.

   All DOM is built via createElement / textContent so user / API strings
   never touch innerHTML. This sidesteps XSS even if a merchant types HTML
   into a product title.

   Wired up via data attributes set in sections/search.liquid:
     [data-vmx-predictive]        — form root
     [data-vmx-predictive-input]  — search input
     [data-vmx-predictive-panel]  — dropdown container (initially hidden)
     [data-vmx-predictive-body]   — where rendered nodes live
*/
(function () {
  'use strict';

  const ROOTS = document.querySelectorAll('[data-vmx-predictive]');
  if (!ROOTS.length) return;

  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 160;
  const PRODUCT_LIMIT = 6;
  const ARTICLE_LIMIT = 3;
  const PAGE_LIMIT = 3;

  const formatMoney = (cents) => {
    if (cents == null) return '';
    return '£' + (Number(cents) / 100).toFixed(2);
  };

  /** Build a fragment where occurrences of `term` inside `text` are wrapped
   *  in <mark>. Splits with a global regex via matchAll — never uses innerHTML. */
  const highlight = (text, term) => {
    const frag = document.createDocumentFragment();
    const str = String(text || '');
    if (!term) {
      frag.appendChild(document.createTextNode(str));
      return frag;
    }
    const safeTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(safeTerm, 'ig');
    const matches = Array.from(str.matchAll(re));
    let cursor = 0;
    for (const m of matches) {
      const start = m.index;
      const end = start + m[0].length;
      if (start > cursor) frag.appendChild(document.createTextNode(str.slice(cursor, start)));
      const mark = document.createElement('mark');
      mark.textContent = m[0];
      frag.appendChild(mark);
      cursor = end;
    }
    if (cursor < str.length) frag.appendChild(document.createTextNode(str.slice(cursor)));
    return frag;
  };

  const el = (tag, opts = {}) => {
    const node = document.createElement(tag);
    if (opts.className) node.className = opts.className;
    if (opts.text != null) node.textContent = opts.text;
    if (opts.id) node.id = opts.id;
    if (opts.attrs) for (const [k, v] of Object.entries(opts.attrs)) node.setAttribute(k, v);
    return node;
  };

  const svgNS = 'http://www.w3.org/2000/svg';
  const makeSvg = (paths) => {
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('width', '16'); svg.setAttribute('height', '16');
    svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round');
    for (const d of paths) {
      const p = document.createElementNS(svgNS, 'path');
      p.setAttribute('d', d);
      svg.appendChild(p);
    }
    return svg;
  };

  const iconJournal = () => {
    const wrap = el('span', { className: 'vmx-search__suggest-icon', attrs: { 'aria-hidden': 'true' } });
    wrap.appendChild(makeSvg(['M4 4h16v16H4z', 'M8 9h8M8 13h8M8 17h5']));
    return wrap;
  };
  const iconPage = () => {
    const wrap = el('span', { className: 'vmx-search__suggest-icon', attrs: { 'aria-hidden': 'true' } });
    wrap.appendChild(makeSvg(['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6']));
    return wrap;
  };

  const buildProduct = (p, term, idx) => {
    const a = el('a', {
      className: 'vmx-search__suggest-item vmx-search__suggest-item--product',
      id: 'vmx-suggest-product-' + idx,
      attrs: { role: 'option', href: p.url || '#', 'data-suggest-item': '' }
    });

    if (p.featured_image) {
      a.appendChild(el('img', {
        className: 'vmx-search__suggest-img',
        attrs: {
          src: String(p.featured_image).replace(/(\.[^./]+)$/, '_120x120$1'),
          alt: '', width: '48', height: '48', loading: 'lazy'
        }
      }));
    } else {
      a.appendChild(el('div', {
        className: 'vmx-search__suggest-img vmx-search__suggest-img--placeholder',
        attrs: { 'aria-hidden': 'true' }
      }));
    }

    const meta = el('span', { className: 'vmx-search__suggest-meta' });
    const title = el('span', { className: 'vmx-search__suggest-title' });
    title.appendChild(highlight(p.title, term));
    meta.appendChild(title);

    const sub = el('span', { className: 'vmx-search__suggest-sub' });
    if (p.vendor) sub.appendChild(el('span', { className: 'vmx-search__suggest-vendor', text: p.vendor }));
    if (p.price != null) sub.appendChild(el('span', { className: 'vmx-search__suggest-price', text: formatMoney(p.price) }));
    if (p.compare_at_price && p.compare_at_price > p.price) {
      sub.appendChild(el('span', { className: 'vmx-search__suggest-compare', text: formatMoney(p.compare_at_price) }));
    }
    meta.appendChild(sub);
    a.appendChild(meta);

    a.appendChild(el('span', { className: 'vmx-search__suggest-arrow', text: '↗', attrs: { 'aria-hidden': 'true' } }));
    return a;
  };

  const buildEditorialItem = (kind, kicker, iconFn, item, term, idx) => {
    const a = el('a', {
      className: 'vmx-search__suggest-item vmx-search__suggest-item--' + kind,
      id: 'vmx-suggest-' + kind + '-' + idx,
      attrs: { role: 'option', href: item.url || '#', 'data-suggest-item': '' }
    });
    a.appendChild(iconFn());
    const meta = el('span', { className: 'vmx-search__suggest-meta' });
    meta.appendChild(el('span', { className: 'vmx-search__suggest-kicker', text: kicker }));
    const title = el('span', { className: 'vmx-search__suggest-title' });
    title.appendChild(highlight(item.title, term));
    meta.appendChild(title);
    a.appendChild(meta);
    return a;
  };

  function attach(form) {
    const input = form.querySelector('[data-vmx-predictive-input]');
    const panel = form.querySelector('[data-vmx-predictive-panel]');
    const body  = form.querySelector('[data-vmx-predictive-body]');
    if (!input || !panel || !body) return;

    let activeIndex = -1;
    let items = [];
    let timer = null;
    let currentTerm = '';
    let abortController = null;

    const clearBody = () => { while (body.firstChild) body.removeChild(body.firstChild); };

    const open = () => {
      if (!panel.hidden) return;
      panel.hidden = false;
      // Force reflow so the transition runs from initial state
      // eslint-disable-next-line no-unused-expressions
      panel.offsetHeight;
      panel.dataset.open = 'true';
      input.setAttribute('aria-expanded', 'true');
    };

    const close = () => {
      panel.dataset.open = 'false';
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-activedescendant', '');
      activeIndex = -1;
      setTimeout(() => {
        if (panel.dataset.open !== 'true') panel.hidden = true;
      }, 180);
    };

    const setActive = (next) => {
      if (!items.length) return;
      const len = items.length;
      activeIndex = ((next % len) + len) % len;
      items.forEach((node, i) => {
        if (i === activeIndex) {
          node.setAttribute('aria-selected', 'true');
          node.classList.add('is-active');
          input.setAttribute('aria-activedescendant', node.id);
          node.scrollIntoView({ block: 'nearest' });
        } else {
          node.removeAttribute('aria-selected');
          node.classList.remove('is-active');
        }
      });
    };

    const renderEmpty = (term) => {
      clearBody();
      const wrap = el('div', { className: 'vmx-search__suggest-empty' });
      const title = el('p', { className: 'vmx-search__suggest-empty-title' });
      title.appendChild(document.createTextNode('No quick matches for "'));
      title.appendChild(document.createTextNode(term));
      title.appendChild(document.createTextNode('"'));
      wrap.appendChild(title);
      const sub = el('p', { className: 'vmx-search__suggest-empty-sub' });
      sub.appendChild(document.createTextNode('Press '));
      sub.appendChild(el('kbd', { text: 'Enter' }));
      sub.appendChild(document.createTextNode(' to search the full catalogue.'));
      wrap.appendChild(sub);
      body.appendChild(wrap);
      items = [];
      activeIndex = -1;
    };

    const renderResults = (data, term) => {
      const results = (data && data.resources && data.resources.results) || {};
      const products = (results.products || []).slice(0, PRODUCT_LIMIT);
      const articles = (results.articles || []).slice(0, ARTICLE_LIMIT);
      const pages    = (results.pages    || []).slice(0, PAGE_LIMIT);

      if (!(products.length + articles.length + pages.length)) {
        renderEmpty(term);
        open();
        return;
      }

      clearBody();

      const addGroup = (label, nodes) => {
        if (!nodes.length) return;
        const group = el('div', { className: 'vmx-search__suggest-group' });
        group.appendChild(el('p', { className: 'vmx-search__suggest-group-label', text: label }));
        nodes.forEach((n) => group.appendChild(n));
        body.appendChild(group);
      };

      addGroup('Products', products.map((p, i) => buildProduct(p, term, i)));
      addGroup('Journal', articles.map((a, i) => buildEditorialItem('article', 'Journal', iconJournal, a, term, i)));
      addGroup('Library', pages.map((pg, i) => buildEditorialItem('page', 'Library', iconPage, pg, term, i)));

      const all = el('a', {
        className: 'vmx-search__suggest-all',
        id: 'vmx-suggest-all',
        attrs: { role: 'option', href: '/search?q=' + encodeURIComponent(term) }
      });
      all.appendChild(document.createTextNode('View all results for '));
      const strong = el('strong');
      strong.appendChild(document.createTextNode('"' + term + '"'));
      all.appendChild(strong);
      all.appendChild(document.createTextNode(' '));
      all.appendChild(el('span', { text: '→', attrs: { 'aria-hidden': 'true' } }));
      body.appendChild(all);

      items = Array.from(body.querySelectorAll('[data-suggest-item], .vmx-search__suggest-all'));
      activeIndex = -1;
      open();
    };

    const fetchSuggestions = async (term) => {
      if (abortController) abortController.abort();
      abortController = new AbortController();

      const url = '/search/suggest.json?q=' + encodeURIComponent(term)
        + '&resources[type]=product,article,page'
        + '&resources[limit]=' + PRODUCT_LIMIT
        + '&resources[options][unavailable_products]=last';

      try {
        const res = await fetch(url, { signal: abortController.signal, headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('predictive fetch failed');
        const data = await res.json();
        if (term !== currentTerm) return;
        renderResults(data, term);
      } catch (err) {
        if (err.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.warn('[vmx-search] predictive fetch error:', err);
      }
    };

    const onInput = (e) => {
      const term = (e.target.value || '').trim();
      currentTerm = term;
      if (timer) clearTimeout(timer);
      if (term.length < MIN_CHARS) { close(); return; }
      timer = setTimeout(() => fetchSuggestions(term), DEBOUNCE_MS);
    };

    const onKeydown = (e) => {
      if (panel.hidden) {
        if (e.key === 'ArrowDown' && currentTerm.length >= MIN_CHARS) fetchSuggestions(currentTerm);
        return;
      }
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); setActive(activeIndex + 1); break;
        case 'ArrowUp':   e.preventDefault(); setActive(activeIndex - 1); break;
        case 'Enter':
          if (activeIndex >= 0 && items[activeIndex]) {
            e.preventDefault();
            window.location.href = items[activeIndex].href;
          }
          break;
        case 'Escape': close(); break;
      }
    };

    const onFocus = () => {
      if (currentTerm.length >= MIN_CHARS) fetchSuggestions(currentTerm);
    };

    const onDocClick = (e) => { if (!form.contains(e.target)) close(); };

    const onItemHover = (e) => {
      const item = e.target.closest('[data-suggest-item], .vmx-search__suggest-all');
      if (!item) return;
      const idx = items.indexOf(item);
      if (idx >= 0) setActive(idx);
    };

    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKeydown);
    input.addEventListener('focus', onFocus);
    body.addEventListener('mousemove', onItemHover);
    document.addEventListener('click', onDocClick);
  }

  ROOTS.forEach(attach);
})();
